/* globals THREE */

import { Config } from 'config';

import { debounce, bind, assign } from 'lodash';

import { resolve, deg2rad } from 'gear';

import { Matrix } from 'cdl';

import { View, Composition, Plugin, fastThrottle } from '../view';

import { OrbitConstraint, OrbitControls } from './orbit-controls';

import { CameraAnimation, cameraToString, cameraStateFromString } from './three-util';

import { EmptyObject3D } from './object3d';

const { Vector3 } = THREE;


export const View3D = View.extend({

  typeName: 'View3D',

  Properties: {

    autoRotate: { type: 'boolean',
      get() {
        return this.controls.autoRotate;
      },
      set( v ) {
        this.controls.autoRotate = v;
      },
    },

    updateTextureOnZoom: { type: 'boolean', def: true },

    zoomToScale: { type: 'number', def: 1.0 },

    lightOffset: { type: 'number', def: 0 },

    selectAndDragEnabled: { type: 'boolean', def: false }
  },

  // [TODO]
  // backSideDarkFactor
  // backSideColor

  get is3D() {
    return true;
  },

  isIddle() {
    return !this.autoRotate && !this.cameraAnim && this.controls && this.controls.isIddle();
  },

  createScene() {
    // textures are managed by the textureView, and tools are drawn there
    return new Composition({ textureType: '3D', main: true });
  },

  // [TODO] Eliminate setter for textureView, or re-link the plugins
  //        to the new one

  // Plugins that target the model and document are linked to the textureView
  initPlugin(plugin) {
    if( plugin.target === 'view' ) {
      this.callParent(plugin);
    }
    else {
      this.textureView.addPlugin(plugin);
    }
  },

  pluginsToDraw() {
    // return filter(this._plugins,{ target: 'view' });
    const r = [];
    const { plugins } = this;
    for( let k = 0, kEnd = plugins.length; k < kEnd; ++k ) {
      const plugin = plugins[k];
      if( plugin.target === 'view' ) {
        r.push( plugin );
      }
    }
    return r;
  },

  insertViewPlugins( plugins ) {
    plugins.insert( 0, [
      Plugin.Renderer3D(),
      Plugin.ViewControls3D(),
    ]);
  },

  initThreeScene() {

    // Create the scene and set the scene size.
    const threeScene = this.threeScene = new THREE.Scene();

    // Create a camera, zoom it out from the model a bit, and add it to the scene.
    const camera = this.camera = new THREE.PerspectiveCamera(45, this.width / this.height, 0.1, 4000);
    camera.position.set(100, 100, 200);

    // Allow to move the camera around the object, works exactly as OrbitControls but
    // without dealing with user interaction
    this.controls = new OrbitConstraint(camera);

    threeScene.add(camera);

    // Set the background color of the scene.
    // this._setBackgroundColor();

    this.addLights();

    this.updateSize();
  },

  addLights() {

    // We tried different light strategies and it was really difficult to get good results using
    // directional and spot lights. To much differences bettwen the light and dark regions.
    // Specially for a white background. The hemisphere light that we were using so far gives
    // the best result. But we change the way the light moves with the camera so we do not have
    // unreal bright spots in right in front of the camera.

    const { threeScene } = this;

    if( Config.lights === 'Old' ) {
      const light = this.light = new THREE.HemisphereLight(0xffffff, 0x808080, 1);
      light.position.set(100, 100, 200);

      threeScene.add(light);
    }
    else {

      if( THREE.REVISION === '73' ) {

        //if( Config.noHemisphereLight ) {
        //  threeScene.add( new THREE.HemisphereLight( 0x111111, 0x444444 ) );
        //}

        threeScene.add( new THREE.AmbientLight( 0xa0a0a0, 0.1 ) );

        const light1 = this.light  = new THREE.PointLight( 0xffffff, Config.lightPower ? parseFloat(Config.lightPower) : 0.4 );
        // var light2 = this.light2 = new THREE.PointLight( 0xffffff, 0.1 );
        // var light3 = this.light3 = new THREE.PointLight( 0xffffff, 0.1 );
        light1.position.set( -1000, 0,  2000 );
        // light2.position.set( -1000, 100, -2000 );
        // light3.position.set(  1000, -100, 2000 );

        const parent = this.camera;
        parent.add(light1);
        // parent.add(light2);
        // parent.add(light3);
      }
      else {

        let ambientLightPower = 0.3, light1Power = 0.6, light2Power = 0.3;
        if( Config.lightPower ) {
          var lightPowerF = Config.lightPower.split(',');
          ambientLightPower = parseFloat(lightPowerF[0]);
          light1Power       = parseFloat(lightPowerF[1]);
          light2Power       = parseFloat(lightPowerF[2]);
        }


        const lightAmbient = this.lightAmbient = new THREE.AmbientLight( 0xffffff, ambientLightPower );
        threeScene.add( lightAmbient );

        const light1 = this.light  = new THREE.PointLight( 0xffffff, light1Power );
        const light2 = this.light2 = new THREE.PointLight( 0xffffff, light2Power );
        //const light3 = this.light3 = new THREE.PointLight( 0xffffff, 0.4 );
        light1.position.set( -1000, 0, 1000 );
        light2.position.set( 500, 1000, 1000 );
        //light3.position.set(  1000, -100, 2000 );

        const parent = this.camera;
        parent.add(light1);
        parent.add(light2);
        //parent.add(light3);
      }
    }
  },

  zoomToFit$() {
    this.zoomToFront();
    return resolve(this);
  },

  objectsBBox3D() {
    return this.object3D.bbox;
  },

  _zoomToFitCameraDistance() {

    const bb = this.objectsBBox3D(),
          objectSize = ( bb.max.y - bb.min.y );

    const { width, height, camera } = this;

    const fov = camera.fov * ( Math.PI / 180 );

    let distance = Math.abs( objectSize / Math.sin( fov / 2 ) ) * 0.6 / this._zoomToScale;
    if( width < height ) {
      distance *= Math.sqrt(height/width);
    }

    return distance;
  },

  _zoomToSide( dirX, dirY, dirZ, msTransition ) {
    const distance = this._zoomToFitCameraDistance();
    const pos = new Vector3(dirX,dirY,dirZ).normalize().multiplyScalar(distance);
    this._zoomToPos( pos, msTransition );
  },

  _zoomToPos( pos, msTransition ) {
    this.controls.autoRotate = false;
    this.setCameraState({ pos, target: new Vector3(0,0,0) },msTransition);
  },

  zoomToFront( msTransition ) {
    this._zoomToSide( 0.4, 0.2, 1, msTransition );
  },

  zoomToBack( msTransition ) {
    this._zoomToSide( 0.4, 0.2, -1, msTransition );
  },

  zoomToRight( msTransition ) {
    this._zoomToSide( 1, 0.2, 0.4, msTransition );
  },

  zoomToLeft( msTransition ) {
    this._zoomToSide( -1, 0.2, 0.4, msTransition );
  },

  rotateCameraRight( angle ) {
    this.rotateModelLeft(angle);
  },
  rotateCameraLeft( angle ) {
    this.rotateModelRight(angle);
  },
  rotateCameraDown( angle ) {
    this.rotateModelUp(angle);
  },
  rotateCameraUp( angle ) {
    this.rotateModelDown(angle);
  },

  rotateModelRight( angle ) {
    this.controls.rotateLeft( deg2rad(angle) );
    this._updateControls();
  },
  rotateModelLeft( angle ) {
    this.rotateModelRight(-angle);
  },
  rotateModelDown( angle ) {
    this.controls.rotateUp( deg2rad(angle) );
    this._updateControls();
  },
  rotateModelUp( angle ) {
    this.rotateModelDown(-angle);
  },

  zoomIn( scale ) {
    this.controls.dollyIn(scale);
    this._updateControls();
  },
  zoomOut( scale ) {
    this.controls.dollyOut(scale);
    this._updateControls();
  },

  _updateControls() {
    const { controls } = this;
    controls.autoRotate = false;
    controls.update();
  },


  get cameraStateString() {
    return cameraToString(this.camera,this.controls.target);
  },
  set cameraStateString( string ) {
    this.setCameraStateString( string, 1000 );
  },

  setCameraStateString( string, msTransition = 0 ){
    this.setCameraState( cameraStateFromString(string), msTransition );
  },
  setCameraState( state, msTransition = 0 ) {
    const { camera, controls } = this;
    if( msTransition === 0 ) {
      camera.position.copy( state.pos    );
      controls.target.copy( state.target );
      camera.updateProjectionMatrix();
      controls.update();
      this.redraw('3D');
    }
    else {
      this.cameraAnim = new CameraAnimation({ camera, controls,
        ms: msTransition,
        to: state,
      });
    }
  },

  onOrbitControlChange() {
    this.redraw('3D');
  },

  triggerZoomEvents() {
    const { zoom } = this.controls;

    // [TODO] This should be responsability of each plugin, avoid invalidating
    // the tools layers if there isn't a tool
    this.redraw('plugins','tools');

    this.fireEvent('zoomupdated',this, zoom);
    this.fireEvent('change',this);
    const { textureView } = this;
    textureView.fireEvent('zoomupdated',textureView,zoom);
    textureView.fireEvent('change',textureView,zoom);
  },
  debouncedTriggerZoomEvents: debounce(function() {
    this.triggerZoomEvents();
  },500),

  onOrbitControlZoomUpdated() {
    if( this.updateTextureOnZoom ) {
      this.triggerZoomEvents();
    }
    else {
      this.debouncedTriggerZoomEvents();
    }
  },

  // This function is triggered once per animation frame
  // We are auto rotating and updating the camera animation here

  draw() {

    this.object3D.drawTextures();

    const { cameraAnim, controls } = this;

    if( this.autoRotate || cameraAnim ) {

      if( cameraAnim ) {
        if( ! cameraAnim.update() ) {
          this.cameraAnim = null;
        }
      }
      controls.update();
      this.redraw('3D');
    }

    if( this.object3D.animate( this.clock.getDelta() ) ) {
      this.redraw('3D');
    }

    this.callParent();
  },

  // This function is issued every time the 3D scene needs to be updated
  // by the Renderer3D plugin

  draw3D( renderer ) {
    const { object3D, camera } = this;
    // console.log('draw3D');
    // Create a dummy 3D object, so we always have something to show
    // It could also serve as a loading animation and simplify the code
    this.setLightPos();
    object3D.draw( renderer, camera );
  },

  get controlsEnabled() {
    return this.controls.enabled;
  },
  set controlsEnabled( state ) {
    const controls = this.controls;
    if( state === false && this._controlsPanning ) {
      controls.onMouseUp();
      this._controlsPanning = false;
    }
    controls.enabled = state;
  },

  // [Review]
  // You need to pass preserveDrawingBuffer at creation if you want to use this function
  image() {
    return this.canvas('3D').toDataURL("image/png");
  },

  setLightPos() {

    const cameraPos = this.camera.position,
          lightOffset = this.lightOffset;

    if( Config.lights === 'Old' ) {

      const lightPos = this.light.position;
      if( lightOffset === 0 ) {
        lightPos.copy( cameraPos );
      }
      else {
        const bb = this.objectsBBox3D();

        const bb_max = bb.max,
            center = bb.center;

        const b = new Vector3(0,lightOffset,0);
        const c = new Vector3().subVectors(cameraPos,center);
        const n = new Vector3().crossVectors(b,c);
        const l = new Vector3().addVectors(center,n);

        l.y = bb_max.y;

        lightPos.copy( new Vector3().addVectors(cameraPos,new Vector3().addVectors(cameraPos,l)) );
      }
    }
  },

  updateSize(width,height) {
    this._previousZoomToFitDistance = this._zoomToFitCameraDistance();
    this.callParent(width,height);
  },

  // Called just before the resize events, after the sizing is done
  postResize() {
    const { camera, controls, width, height } = this;

    this.callParent();

    camera.aspect = width / height;
    camera.updateProjectionMatrix();

    // For width less than height aspect ratio, we need to move the camera manually to maintain
    // the same position of the object in the 3D view
    const newZoomToFitDistance = this._zoomToFitCameraDistance();
    camera.position.multiplyScalar( newZoomToFitDistance / this._previousZoomToFitDistance );
    camera.updateProjectionMatrix();

    controls.update();
  },

  fireResizeEvent() {
    this.textureView.fireResizeEvent();
    this.callParent();
  },

  init( config = {} ) {

    this.clock = new THREE.Clock();

    this.object3D = new EmptyObject3D();

    this.callParent(config);

    this.initThreeScene();
  },

  appendTo( domElement, id ) {

    this.callParent(domElement,id);

    // Upgrade OrbitConstraint to OrbitControls so that we can pan around with the mouse
    const controls = this.controls = new OrbitControls(this.camera,domElement);
    this._controlsPanning = false;

    // Disable panning, orbit looks weirds after using it
    assign( controls, {
      autoRotate      : false,
      autoRotateSpeed : 5.0,
      enableKeys      : false,
      enableDamping   : true,
      dampingFactor   : 0.96
    });

    controls.addEventListener('change',bind(this.onOrbitControlChange,this));
    controls.addEventListener('zoomupdated',bind(this.onOrbitControlZoomUpdated,this));

    return this;
  },

  // [TODO]

  getViewPortTransform() {
    return new Matrix();
  },
});

// We could simplify this by making OrbitControls a plugin

Plugin.ViewControls3D = Plugin.extend({

  target: 'view',

  initPlugin() {
    this.panning = false;
  },

  on_pointerdown({ view, event }) {
    this.panning = true;
    view.controls.on_pointerdown(event);
  },

  on_pointermove: fastThrottle( function({ view, event }) {
    if( this.panning ) {
      view.controls.on_pointermove(event);
    }
  }, 10),

  on_pointerup({ view, event }) {
    if( this.panning ) {
      this.panning = false;
      view.controls.on_pointerup(event);
    }
  },

  on_mousewheel({ view, event }) {
    this.panning = false;
    view.controls.on_mousewheel(event);
  }
});



/** WEBPACK FOOTER **
 ** ./src/ui/canvas3d/view3d.js
 **/